import { defineStore } from 'pinia'
import type {
  PrepareApiRequest,
  CreateSwapOrdersRequest,
  ClosePositionsRequest,
  CreateSwapOrderRequest,
} from '@/services/trade-v2-service/types'
import { TradeV2Service } from '@/services/trade-v2-service'
import { useRequestFactory } from './factories/requestFactory'
import type { ApiKeyType } from '@/services/api-keys-v2-service/types'

type ActionType =
  | 'prepare'
  | 'createSwapOrders'
  | 'closeSwapPositions'
  | 'cancelSwapOrders'
  | 'createSwapOrder'
  | 'createSwapOrderPro'
  | 'balance'
  | 'prepareSingleProfile'

export const useTradeStore = defineStore('trade', () => {
  const { loading, errors, requestHandler } = useRequestFactory<ActionType>()

  const prepare = async (profileNum: number, data: PrepareApiRequest) => {
    return await requestHandler('prepare', async () => {
      const req = await TradeV2Service.prepareProfile(profileNum, data)
      return req.data
    })
  }

  const fetchBalance = async (
    exchange: string,
    profileNum: number,
    marketType: 'spot' | 'swap',
    keyType: ApiKeyType,
  ) => {
    return await requestHandler('balance', async () => {
      const req = await TradeV2Service.fetchBalance(exchange, profileNum, marketType, keyType)
      return req.data
    })
  }

  const createSwapOrders = async (profileNum: number, data: CreateSwapOrdersRequest) => {
    return await requestHandler('createSwapOrders', async () => {
      const req = await TradeV2Service.createSwapOrders(profileNum, data)
      return req.data
    })
  }

  const closeSwapPositions = async (profileNum: number, data: ClosePositionsRequest) => {
    return await requestHandler('closeSwapPositions', async () => {
      const req = await TradeV2Service.closeSwapPositions(profileNum, data)
      return req.data
    })
  }

  const cancelSwapOrders = async (profileNum: number, data: ClosePositionsRequest) => {
    return await requestHandler('cancelSwapOrders', async () => {
      const req = await TradeV2Service.cancelSwapOrders(profileNum, data)
      return req.data
    })
  }

  // const createSwapOrder = async (profileNum: number, data: CreateSwapOrderRequest) => {
  //   return await requestHandler('createSwapOrder', async () => {
  //     const req = await TradeV2Service.createSwapOrder(profileNum, data)
  //     return req.data
  //   })
  // }

  const createSwapOrderPro = async (
    profileNum: number,
    data: CreateSwapOrderRequest,
    keyType: ApiKeyType,
  ) => {
    return await requestHandler('createSwapOrderPro', async () => {
      const req = await TradeV2Service.createSwapOrderPro(profileNum, data, keyType)
      return req.data
    })
  }

  const prepareSingleProfile = async (
    exchange: string,
    asset: string,
    profileNum: number,
    keyType: ApiKeyType,
    leverage: number,
    marginMode: 'isolated' | 'cross',
    quote?: string,
  ) => {
    return await requestHandler('prepareSingleProfile', async () => {
      const req = await TradeV2Service.prepareSingleProfile(
        exchange,
        asset,
        profileNum,
        keyType,
        leverage,
        marginMode,
        quote,
      )
      return req.data
    })
  }

  return {
    loading,
    errors,
    prepare,
    prepareSingleProfile,
    fetchBalance,
    createSwapOrders,
    closeSwapPositions,
    cancelSwapOrders,
    createSwapOrderPro,
  }
})
